import {
  IonIcon,
  IonLabel,
  IonList,
  IonListHeader,
  IonText,
} from '@ionic/react';
import { heart, heartOutline } from 'ionicons/icons';
import { capitalize, groupBy, isNumber, orderBy, remove } from 'lodash';
import { ReactElement, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { FaCircle, FaRegCircle } from 'react-icons/fa';
import { Machine } from '../../api/graphql/generated';
import { MonitorIOBindingResult } from '../../api/hooks/binding/binding.types';
import { useMachineIO } from '../../api/hooks/machine-io.hooks';
import {
  useFavoritesHelpers,
  useMachineIOFavorites,
} from '../../store/favorites/favorites.hooks';
import { IonItem } from '../ionic/IonItem';
import { getIOFavoriteKey, getIOStandardName } from './io.utils';

interface MachineDetailsProps {
  machine?: Machine;
}

interface SectionItem<T> {
  title: string;
  data: T[];
}

const MachineIO = (props: MachineDetailsProps): ReactElement => {
  const { machine } = props;
  const { i18n } = useTranslation();

  const io = useMachineIO({ id: machine?.id });

  const favIO = useMachineIOFavorites(machine);
  const { addMachineFavoriteIO, removeMachineFavoriteIO } =
    useFavoritesHelpers();

  const toggleFavorite = (io: MonitorIOBindingResult) => {
    if (machine) {
      const favName = getIOFavoriteKey(io);
      if (favIO?.includes(favName)) {
        removeMachineFavoriteIO({ favorite: favName, machine });
      } else {
        addMachineFavoriteIO({ favorite: favName, machine });
      }
    }
  };

  const sections = prepareSections(io);

  if (sections.length === 0) {
    return (
      <div className="full-height-width content-center">
        <IonLabel>{i18n.t('common:machine_io_waiting_for_data')}</IonLabel>
      </div>
    );
  } else {
    return (
      <IonList lines="full">
        {sections.map((section, sectionIndex) => {
          return (
            <div key={sectionIndex}>
              <IonListHeader lines="full">
                <IonLabel>{section.title}</IonLabel>
              </IonListHeader>
              {section.data.map((item, index) => {
                const isFavorited =
                  favIO?.includes(getIOFavoriteKey(item)) ?? false;
                const stdIOName = getIOStandardName(item);
                return (
                  <IonItem key={index}>
                    <IonIcon
                      className="ion-padding-end"
                      color={isFavorited ? 'danger' : 'medium'}
                      icon={isFavorited ? heart : heartOutline}
                      size="small"
                      onClick={() => toggleFavorite(item)}
                    />
                    <IonLabel>
                      <h2>{item.label ?? stdIOName}</h2>
                      <p>{stdIOName}</p>
                    </IonLabel>
                    <IOIcon io={item} />
                  </IonItem>
                );
              })}
            </div>
          );
        })}
      </IonList>
    );
  }
};

const MachineIOMemoized = memo(MachineIO);

const IOIcon = ({ io }: { io: MonitorIOBindingResult }): ReactElement => {
  const isValueNumeric = isNumber(io.value);
  let rightElement: ReactElement = <>{String(io.value)}</>;
  if (!isValueNumeric) {
    const Icon = io.value === true ? FaCircle : FaRegCircle;
    const color = io.value === true ? 'success' : 'medium';
    rightElement = (
      <IonText color={color}>
        <Icon size={24} />
      </IonText>
    );
  }
  return (
    <IonText className="ion-text-end" color="medium" slot="end">
      {rightElement}
    </IonText>
  );
};

const prepareSections = (
  machineIO: MonitorIOBindingResult[]
): Array<SectionItem<MonitorIOBindingResult>> => {
  const groupedData = groupBy(
    machineIO,
    (el) => `${capitalize(el.group)} ${capitalize(el.type)}`
  );
  const sectionedData: Array<SectionItem<MonitorIOBindingResult>> = [];
  Object.entries(groupedData).forEach(([section, unSortedData]) => {
    const data = orderBy<MonitorIOBindingResult>(
      unSortedData,
      ['name'],
      ['asc']
    );
    sectionedData.push({ title: section, data });
  });
  const standardSections = remove(sectionedData, (sec) =>
    sec.title.match(/Standard/)
  );
  const configSections = remove(sectionedData, (sec) =>
    sec.title.match(/Config/)
  );

  return [...standardSections, ...configSections, ...sectionedData];
};

export { MachineIOMemoized as MachineIO, IOIcon };
